import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { History, Film, Calendar, Trash2, Search, Filter, ChevronDown, ChevronUp, X, Heart, Eye, EyeOff, Play, Loader } from 'lucide-react';
import { addToFavorites, removeFromFavorites, isFavorite, markAsWatched, markAsUnwatched, isWatched, registerGlobalRefresh } from '../services/favoriteService';
import { getMovieTrailer } from '../services/tmdbService';
import TrailerModal from '../components/TrailerModal';

const HistoryPage = () => {
    // Görev 7: Auth kontrolü
    const { user, authStatus } = useAuth();
    const { t, formatDateTime } = useLanguage();
    const [recommendations, setRecommendations] = useState([]);
    const [filteredRecommendations, setFilteredRecommendations] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedGenres, setSelectedGenres] = useState([]);
    const [sortBy, setSortBy] = useState('date');
    const [expandedItems, setExpandedItems] = useState(new Set());
    const [selectedMovie, setSelectedMovie] = useState(null); // Detay modal için
    const [refreshKey, setRefreshKey] = useState(0); // Favori ve izleme durumu için refresh

    // Pagination state'leri
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(5); // Sayfa başına 5 öneri

    // Trailer modal state'leri
    const [selectedTrailer, setSelectedTrailer] = useState(null);
    const [isTrailerModalOpen, setIsTrailerModalOpen] = useState(false);
    const [trailerLoading, setTrailerLoading] = useState(false);
    const [currentMovieForTrailer, setCurrentMovieForTrailer] = useState('');

    // Güvenli şekilde Google araması açar
    const openGoogleSearch = (title = '') => {
        const safeTitle = title.trim();
        if (!safeTitle) return;
        const q = encodeURIComponent(`${safeTitle} full izle`);
        window.open(`https://www.google.com/search?q=${q}`, "_blank", "noopener,noreferrer");
    };

    // Ruh hali çeviri fonksiyonu
    const translateMoodToTurkish = (text) => {
        if (!text) return text;

        const moodTranslations = {
            'happy': 'mutlu',
            'sad': 'hüzünlü',
            'excited': 'heyecanlı',
            'relaxed': 'rahat',
            'romantic': 'romantik',
            'adventure': 'macera arayan'
        };

        let translatedText = text;
        Object.entries(moodTranslations).forEach(([english, turkish]) => {
            const regex = new RegExp(`\\b${english}\\b`, 'gi');
            translatedText = translatedText.replace(regex, turkish);
        });

        return translatedText;
    };

    // Görev 7: Auth kontrolü ekle
    useEffect(() => {
        // Auth hazır değilse bekle
        if (authStatus !== 'authenticated' || !user?.id) {
            return;
        }

        loadRecommendations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshKey, authStatus, user?.id]); // refreshKey ve auth durumu değişince yeniden yükle

    useEffect(() => {
        filterAndSortRecommendations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recommendations, searchQuery, selectedGenres, sortBy, refreshKey]); // refreshKey ekle

    useEffect(() => {
        // Sayfa değiştiğinde en üste scroll
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [currentPage]);

    // Global refresh sistemini kaydet (sonsuz döngü önleme ile)
    useEffect(() => {
        const unregister = registerGlobalRefresh(() => {
            // Sadece verileri yeniden yükle, setRefreshKey kullanma
            loadRecommendations();
        });

        return unregister; // Cleanup
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadRecommendations = () => {
        const recKey = user?.id ? `film_recommendations:${user.id}` : 'film_recommendations';
        const saved = localStorage.getItem(recKey);
        if (saved) {
            setRecommendations(JSON.parse(saved));
        }
    };

    const deleteRecommendation = async (index) => {
        const updated = recommendations.filter((_, i) => i !== index);
        setRecommendations(updated);
        const recKey = user?.id ? `film_recommendations:${user.id}` : 'film_recommendations';
        localStorage.setItem(recKey, JSON.stringify(updated));

        // Supabase tablosundan sil (yeni sistem)
        if (user?.id) {
            try {
                const { saveAllRecommendationsToSupabase } = await import('../services/supabaseDataService');
                await saveAllRecommendationsToSupabase(updated, user.id);
            } catch (error) {
                console.error('❌ Öneri silme hatası:', error);
            }
        }
    };

    const clearAllHistory = async () => {
        if (window.confirm(t('history.deleteAllConfirm'))) {
            setRecommendations([]);
            const recKey = user?.id ? `film_recommendations:${user.id}` : 'film_recommendations';
            localStorage.removeItem(recKey);

            // Supabase tablosundan sil (yeni sistem)
            if (user?.id) {
                try {
                    const { saveAllRecommendationsToSupabase } = await import('../services/supabaseDataService');
                    await saveAllRecommendationsToSupabase([], user.id);
                } catch (error) {
                    console.error('❌ Öneriler temizleme hatası:', error);
                }
            }
        }
    };

    const filterAndSortRecommendations = () => {
        let filtered = [...recommendations];

        // Yeni filtreleme yapıldığında sayfa 1'e dön
        const shouldResetPage = currentPage > 1;
        if (shouldResetPage) {
            setCurrentPage(1);
        }

        // Arama filtresi
        if (searchQuery) {
            filtered = filtered.filter(rec =>
                rec.explanation.toLowerCase().includes(searchQuery.toLowerCase()) ||
                rec.recommendations.some(movie =>
                    movie.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    movie.genre.toLowerCase().includes(searchQuery.toLowerCase())
                )
            );
        }

        // Tür filtresi
        if (selectedGenres.length > 0) {
            filtered = filtered.filter(rec =>
                rec.recommendations.some(movie =>
                    selectedGenres.includes(movie.genre)
                )
            );
        }

        // Sıralama
        filtered.sort((a, b) => {
            switch (sortBy) {
                case 'date':
                    return new Date(b.generatedAt) - new Date(a.generatedAt);
                case 'count':
                    return b.recommendations.length - a.recommendations.length;
                default:
                    return 0;
            }
        });

        setFilteredRecommendations(filtered);
    };

    // Pagination hesaplamaları
    const totalPages = Math.ceil(filteredRecommendations.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentRecommendations = filteredRecommendations.slice(startIndex, endIndex);

    // Sayfa değiştirme fonksiyonu
    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        setExpandedItems(new Set()); // Sayfa değiştiğinde tüm expanded öğeleri kapat
    };

    // Pagination numaraları oluşturma
    const getPaginationNumbers = () => {
        const numbers = [];
        const maxVisiblePages = 5;

        if (totalPages <= maxVisiblePages) {
            // Toplam sayfa sayısı 5'ten az ise hepsini göster
            for (let i = 1; i <= totalPages; i++) {
                numbers.push(i);
            }
        } else {
            // 5'ten fazla ise akıllı sayfa numarası gösterimi
            if (currentPage <= 3) {
                // Başlarda ise 1,2,3,4,5 göster
                for (let i = 1; i <= 5; i++) {
                    numbers.push(i);
                }
            } else if (currentPage >= totalPages - 2) {
                // Sonlarda ise son 5 sayfayı göster
                for (let i = totalPages - 4; i <= totalPages; i++) {
                    numbers.push(i);
                }
            } else {
                // Ortada ise mevcut sayfa etrafındaki 5 sayfayı göster
                for (let i = currentPage - 2; i <= currentPage + 2; i++) {
                    numbers.push(i);
                }
            }
        }

        return numbers;
    };

    const toggleExpanded = (index) => {
        const newExpanded = new Set(expandedItems);
        if (newExpanded.has(index)) {
            newExpanded.delete(index);
        } else {
            newExpanded.add(index);
        }
        setExpandedItems(newExpanded);
    };

    const getAllGenres = () => {
        const genres = new Set();
        recommendations.forEach(rec => {
            rec.recommendations.forEach(movie => {
                genres.add(movie.genre);
            });
        });
        return Array.from(genres).sort();
    };

    const toggleGenreFilter = (genre) => {
        setSelectedGenres(prev =>
            prev.includes(genre)
                ? prev.filter(g => g !== genre)
                : [...prev, genre]
        );
    };

    // Emoji formatını parse etme fonksiyonu
    const parseMovieDescription = (description) => {
        if (!description) return {};

        const lines = description.split('\n');
        const parsed = {};

        lines.forEach(line => {
            if (line.includes('🎬 Film Adı:')) {
                parsed.title = line.replace('🎬 Film Adı:', '').trim();
            } else if (line.includes('📆 Yıl:')) {
                parsed.year = line.replace('📆 Yıl:', '').trim();
            } else if (line.includes('🌟 IMDb Puanı:')) {
                parsed.rating = line.replace('🌟 IMDb Puanı:', '').trim();
            } else if (line.includes('📝 Kısa Açıklama:')) {
                parsed.explanation = line.replace('📝 Kısa Açıklama:', '').trim();
            } else if (line.includes('🖼️ Poster URL:')) {
                parsed.posterUrl = line.replace('🖼️ Poster URL:', '').trim();
            }
        });

        return parsed;
    };

    // Film kartına tıklama
    const handleMovieClick = (movie) => {
        setSelectedMovie(movie);
    };

    // Modal kapatma
    const closeModal = () => {
        setSelectedMovie(null);
    };

    // Fragman açma fonksiyonu
    const handleWatchTrailer = async (movie) => {
        try {
            setTrailerLoading(true);
            setCurrentMovieForTrailer(movie.title);

            const trailer = await getMovieTrailer(movie.title, movie.year);

            setSelectedTrailer(trailer);
            setIsTrailerModalOpen(true);

        } catch (error) {
            console.error('❌ Fragman yükleme hatası:', error);
            // Hata durumunda da modal aç (fragman bulunamadı mesajı için)
            setSelectedTrailer(null);
            setIsTrailerModalOpen(true);
        } finally {
            setTrailerLoading(false);
        }
    };

    // Trailer modal kapatma
    const closeTrailerModal = () => {
        setIsTrailerModalOpen(false);
        setSelectedTrailer(null);
        setCurrentMovieForTrailer('');
    };

    // Favori durumu toggle
    const handleFavoriteToggle = (movie) => {
        const movieId = movie.id;
        const isCurrentlyFavorite = isFavorite(movieId, movie.title);

        if (isCurrentlyFavorite) {
            removeFromFavorites(movieId, movie.title);
        } else {
            addToFavorites(movie);
        }

        // Component'ı yeniden render etmek için manual refresh
        setRefreshKey(prev => prev + 1);

        // Eğer modal açıksa, modal state'ini de güncelle
        if (selectedMovie && selectedMovie.id === movieId) {
            setSelectedMovie({ ...selectedMovie });
        }
    };

    // İzleme durumu toggle
    const handleWatchedToggle = (movie) => {
        const movieId = movie.id;
        const isCurrentlyWatched = isWatched(movieId);

        if (isCurrentlyWatched) {
            markAsUnwatched(movieId);
        } else {
            markAsWatched(movie);
        }

        // Component'ı yeniden render etmek için manual refresh
        setRefreshKey(prev => prev + 1);

        // Eğer modal açıksa, modal state'ini de güncelle
        if (selectedMovie && selectedMovie.id === movieId) {
            setSelectedMovie({ ...selectedMovie });
        }
    };

    // Detay Modal Komponenti
    const MovieDetailModal = ({ movie, onClose }) => {
        if (!movie) return null;

        const parsedInfo = parseMovieDescription(movie.description);

        // Modal dışına tıklandığında kapatma fonksiyonu
        const handleOverlayClick = (e) => {
            if (e.target === e.currentTarget) {
                onClose();
            }
        };

        return (
            <div
                className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
                onClick={handleOverlayClick}
            >
                <div
                    className="bg-white dark:bg-gray-800 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Close Button */}
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 z-10 bg-black bg-opacity-70 text-white p-2 rounded-full hover:bg-opacity-90 transition-all"
                    >
                        <X className="h-6 w-6" />
                    </button>

                    {/* Main Content - Horizontal Layout */}
                    <div className="flex flex-col lg:flex-row">
                        {/* Left Side - Poster */}
                        <div className="lg:w-1/3 p-6">
                            <div className="relative aspect-[2/3] bg-gradient-to-br from-purple-400 via-purple-500 to-indigo-600 rounded-xl overflow-hidden shadow-xl">
                                {(movie.posterUrl || parsedInfo.posterUrl) && (movie.posterUrl || parsedInfo.posterUrl) !== 'https://via.placeholder.com/400x600/6366f1/ffffff?text=Film+Posteri' ? (
                                    <img
                                        src={movie.posterUrl || parsedInfo.posterUrl}
                                        alt={movie.title}
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <div className="flex items-center justify-center h-full text-white bg-gradient-to-br from-purple-500 via-indigo-600 to-blue-700">
                                        <div className="text-center p-4">
                                            <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-6 border border-white/30">
                                                <Film className="h-20 w-20 mx-auto mb-4 opacity-90" />
                                                <p className="text-xl font-bold mb-2">{movie.title}</p>
                                                <p className="text-sm opacity-80">{parsedInfo.year || movie.year}</p>
                                                <div className="mt-3 flex items-center justify-center space-x-1">
                                                    <span className="text-yellow-300 text-lg">⭐</span>
                                                    <span className="text-base font-medium">{parsedInfo.rating || movie.rating}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Rating Badge on Poster */}
                                <div className="absolute top-3 right-3 bg-black bg-opacity-80 text-white px-3 py-1 rounded-full">
                                    <span className="font-bold text-sm">🌟 {parsedInfo.rating || movie.rating}</span>
                                </div>

                                {/* Durum Göstergeleri */}
                                <div className="absolute top-3 left-3 flex flex-col gap-2">
                                    {isFavorite(movie.id, movie.title) && (
                                        <div className="bg-red-500 bg-opacity-90 text-white p-2 rounded-full shadow-lg">
                                            <Heart className="h-4 w-4 fill-current" />
                                        </div>
                                    )}
                                    {isWatched(movie.id) && (
                                        <div className="bg-green-500 bg-opacity-90 text-white p-2 rounded-full shadow-lg">
                                            <Eye className="h-4 w-4 fill-current" />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Right Side - Movie Info */}
                        <div className="lg:w-2/3 p-6 lg:pl-0">
                            {/* Movie Title */}
                            <div className="mb-6">
                                <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                                    🎬 {parsedInfo.title || movie.title}
                                </h1>
                            </div>

                            {/* Movie Stats */}
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                                <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 dark:from-yellow-900/20 dark:to-yellow-800/20 p-4 rounded-xl text-center">
                                    <div className="text-2xl font-bold text-yellow-700 dark:text-yellow-400">
                                        {parsedInfo.rating || movie.rating}
                                    </div>
                                    <div className="text-sm text-yellow-600 dark:text-yellow-500 font-medium">IMDb Puanı</div>
                                </div>
                                <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 p-4 rounded-xl text-center">
                                    <div className="text-2xl font-bold text-blue-700 dark:text-blue-400">
                                        {parsedInfo.year || movie.year}
                                    </div>
                                    <div className="text-sm text-blue-600 dark:text-blue-500 font-medium">{t('movie.productionYear')}</div>
                                </div>
                                <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 p-4 rounded-xl text-center">
                                    <div className="text-2xl font-bold text-green-700 dark:text-green-400">
                                        {movie.duration || '120'}
                                    </div>
                                    <div className="text-sm text-green-600 dark:text-green-500 font-medium">{t('movie.minutes')}</div>
                                </div>
                                <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 p-4 rounded-xl text-center">
                                    <div className="text-lg font-bold text-purple-700 dark:text-purple-400">
                                        {movie.genre}
                                    </div>
                                    <div className="text-sm text-purple-600 dark:text-purple-500 font-medium">{t('movie.genre')}</div>
                                </div>
                            </div>

                            {/* Cast */}
                            <div className="mb-6">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                                    🎭 <span className="ml-2">{t('movie.actors')}</span>
                                </h3>
                                <div className="flex flex-wrap gap-2">
                                    {(movie.actors || ['Bilinmeyen', 'Oyuncu', 'İsimleri']).map((actor, index) => (
                                        <span key={index} className="px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
                                            {actor}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Bottom Section - Actions, Plot and Recommendation */}
                    <div className="px-6 pb-6 space-y-6">
                        {/* Favori ve İzleme Butonları */}
                        <div className="flex gap-3 justify-center flex-wrap">
                            <button
                                onClick={() => handleFavoriteToggle(movie)}
                                className={`flex items-center space-x-2 px-4 py-2.5 rounded-xl font-medium transition-all transform hover:scale-105 ${isFavorite(movie.id, movie.title)
                                    ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 border-2 border-red-200 dark:border-red-700'
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border-2 border-gray-200 dark:border-gray-600 hover:bg-red-50 dark:hover:bg-red-900/20'
                                    }`}
                            >
                                <Heart className={`h-5 w-5 ${isFavorite(movie.id, movie.title) ? 'fill-current' : ''}`} />
                                <span className="hidden sm:inline">{isFavorite(movie.id, movie.title) ? t('favorites.removeFromFavorites') : t('favorites.addToFavorites')}</span>
                            </button>

                            <button
                                onClick={() => handleWatchedToggle(movie)}
                                className={`flex items-center space-x-2 px-4 py-2.5 rounded-xl font-medium transition-all transform hover:scale-105 ${isWatched(movie.id)
                                    ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 border-2 border-green-200 dark:border-green-700'
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border-2 border-gray-200 dark:border-gray-600 hover:bg-green-50 dark:hover:bg-green-900/20'
                                    }`}
                            >
                                {isWatched(movie.id) ? (
                                    <Eye className="h-5 w-5 fill-current" />
                                ) : (
                                    <EyeOff className="h-5 w-5" />
                                )}
                                <span className="hidden sm:inline">{isWatched(movie.id) ? t('favorites.watchedShort') : t('favorites.markAsWatched')}</span>
                            </button>

                            <button
                                onClick={() => handleWatchTrailer(movie)}
                                disabled={trailerLoading && currentMovieForTrailer === movie.title}
                                className="flex items-center space-x-2 px-4 py-2.5 rounded-xl font-medium transition-all transform hover:scale-105 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border-2 border-blue-200 dark:border-blue-700 hover:bg-blue-200 dark:hover:bg-blue-900/50 disabled:opacity-50"
                            >
                                {trailerLoading && currentMovieForTrailer === movie.title ? (
                                    <Loader className="h-5 w-5 animate-spin" />
                                ) : (
                                    <Play className="h-5 w-5" />
                                )}
                                <span className="hidden sm:inline">
                                    {trailerLoading && currentMovieForTrailer === movie.title
                                        ? t('favorites.searching')
                                        : t('favorites.watchTrailer')
                                    }
                                </span>
                            </button>

                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    openGoogleSearch(movie.title || movie.name);
                                }}
                                className="flex items-center justify-center space-x-1 px-3 py-2 rounded-lg text-xs font-medium transition-all transform hover:scale-105 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 border border-purple-200 dark:border-purple-700 hover:bg-purple-200 dark:hover:bg-purple-900/50"
                            >
                                <Search className="h-4 w-4" />
                                <span className="hidden sm:inline">{t('favorites.searchOnGoogle')}</span>
                            </button>
                        </div>

                        {/* Plot */}
                        <div>
                            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                                📖 <span className="ml-2">{t('favorites.moviePlot')}</span>
                            </h3>
                            <div className="bg-gradient-to-r from-gray-50 to-blue-50 dark:from-gray-700 dark:to-gray-600 rounded-xl p-6">
                                <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-lg">
                                    {movie.plot || t('favorites.noPlotAvailable')}
                                </p>
                            </div>
                        </div>

                        {/* Why Recommended */}
                        {parsedInfo.explanation && (
                            <div>
                                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                                    💡 <span className="ml-2">{t('recommendation.whyRecommended')}</span>
                                </h3>
                                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-xl p-6 border-l-4 border-purple-500">
                                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-lg">
                                        {translateMoodToTurkish(parsedInfo.explanation)}
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    if (recommendations.length === 0) {
        return (
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="text-center">
                    <History className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                        Henüz Geçmiş Öneriniz Yok
                    </h1>
                    <p className="text-gray-600 dark:text-gray-400 mb-8">
                        Film önerileri aldıktan sonra burada geçmişinizi görüntüleyebileceksiniz.
                    </p>
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8">
                        <div className="flex items-center justify-center space-x-4">
                            <div className="w-12 h-16 bg-gray-200 dark:bg-gray-700 rounded flex items-center justify-center">
                                <Film className="h-6 w-6 text-gray-400" />
                            </div>
                            <div className="text-left">
                                <h3 className="font-medium text-gray-900 dark:text-white">
                                    İlk film önerinizi alın
                                </h3>
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                    Yapay zeka destekli kişisel öneriler
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8 py-4 sm:py-8">
            <div className="text-center mb-6 sm:mb-8">
                <div className="flex justify-center items-center space-x-2 mb-4">
                    <History className="h-6 w-6 sm:h-8 sm:w-8 text-purple-600" />
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">{t('history.recommendationHistory')}</h1>
                </div>
                <p className="text-gray-600 dark:text-gray-400 px-4">
                    {t('history.viewAllRecommendations')}
                </p>
            </div>

            {/* Filtreler ve Arama */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 mb-8 space-y-6">
                {/* Üst Kısım: Arama ve Sıralama */}
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                    {/* Arama Bölümü */}
                    <div className="flex-1 max-w-md">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            🔍 {t('history.searchMovie')}
                        </label>
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder={t('history.searchPlaceholder')}
                                className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                            />
                        </div>
                    </div>

                    {/* Sağ Taraf: Sıralama ve Temizle */}
                    <div className="flex flex-col sm:flex-row gap-4">
                        {/* Sıralama */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                📅 {t('history.sorting')}
                            </label>
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                className="px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500 min-w-[140px] transition-all"
                            >
                                <option value="date">{t('history.byDate')}</option>
                            </select>
                        </div>

                        {/* Temizle Butonu */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                🗑️ {t('history.clear')}
                            </label>
                            <button
                                onClick={clearAllHistory}
                                className="px-6 py-3 text-red-600 hover:text-white hover:bg-red-600 border border-red-300 hover:border-red-600 rounded-lg transition-all duration-200 flex items-center space-x-2 min-w-[120px] justify-center"
                            >
                                <Trash2 className="h-4 w-4" />
                                <span className="font-medium">{t('history.deleteAll')}</span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Orta Çizgi */}
                <div className="border-t border-gray-200 dark:border-gray-700"></div>

                {/* Alt Kısım: Tür Filtreleri */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
                        🎭 {t('history.movieGenres')}
                    </label>
                    <div className="flex flex-wrap gap-3">
                        {getAllGenres().map(genre => (
                            <button
                                key={genre}
                                onClick={() => toggleGenreFilter(genre)}
                                className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${selectedGenres.includes(genre)
                                    ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg transform scale-105'
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 hover:shadow-md'
                                    }`}
                            >
                                {genre}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Aktif Filtreler */}
                {(searchQuery || selectedGenres.length > 0) && (
                    <>
                        <div className="border-t border-gray-200 dark:border-gray-700"></div>
                        <div className="flex flex-wrap items-center gap-3">
                            <span className="text-sm font-medium text-purple-600 dark:text-purple-400">✨ Aktif filtreler:</span>
                            {searchQuery && (
                                <span className="px-3 py-1.5 bg-gradient-to-r from-blue-100 to-blue-200 dark:from-blue-900/50 dark:to-blue-800/50 text-blue-800 dark:text-blue-300 text-sm rounded-lg font-medium">
                                    🔍 {searchQuery}
                                </span>
                            )}
                            {selectedGenres.map(genre => (
                                <span key={genre} className="px-3 py-1.5 bg-gradient-to-r from-purple-100 to-purple-200 dark:from-purple-900/50 dark:to-purple-800/50 text-purple-800 dark:text-purple-300 text-sm rounded-lg font-medium">
                                    🎭 {genre}
                                </span>
                            ))}
                            <button
                                onClick={() => {
                                    setSearchQuery('');
                                    setSelectedGenres([]);
                                }}
                                className="px-3 py-1.5 text-sm text-gray-600 hover:text-white hover:bg-gray-600 border border-gray-300 hover:border-gray-600 rounded-lg transition-all duration-200 font-medium"
                            >
                                ✕ {t('history.clearFilters')}
                            </button>
                        </div>
                    </>
                )}
            </div>

            {/* Sonuçlar */}
            <div className="space-y-6">
                {filteredRecommendations.length === 0 ? (
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
                        <Filter className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-500 dark:text-gray-400">
                            Filtre kriterleriyle eşleşen öneri bulunamadı.
                        </p>
                    </div>
                ) : (
                    currentRecommendations.map((recommendation, originalIndex) => {
                        // Orijinal index'i pagination için hesapla
                        const index = startIndex + originalIndex;
                        return (
                            <div key={index} className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                                {/* Başlık */}
                                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center space-x-4">
                                            <div className="flex items-center space-x-2">
                                                <Calendar className="h-5 w-5 text-gray-400" />
                                                <span className="text-sm text-gray-500 dark:text-gray-400">
                                                    {formatDateTime(recommendation.generatedAt, {
                                                        year: 'numeric',
                                                        month: 'long',
                                                        day: 'numeric',
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    })}
                                                </span>
                                            </div>
                                            <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-300 text-xs rounded">
                                                {recommendation.recommendations.length} {t('history.movies')}
                                            </span>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <button
                                                onClick={() => toggleExpanded(index)}
                                                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                            >
                                                {expandedItems.has(index) ? (
                                                    <ChevronUp className="h-5 w-5" />
                                                ) : (
                                                    <ChevronDown className="h-5 w-5" />
                                                )}
                                            </button>
                                            <button
                                                onClick={() => deleteRecommendation(index)}
                                                className="p-1 text-red-400 hover:text-red-600"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </div>
                                    <p className="mt-2 text-gray-700 dark:text-gray-300">
                                        {translateMoodToTurkish(recommendation.explanation)}
                                    </p>
                                </div>

                                {/* Film Listesi */}
                                {expandedItems.has(index) && (
                                    <div className="p-6">
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                            {recommendation.recommendations.map((movie) => {
                                                const parsedInfo = parseMovieDescription(movie.description);

                                                return (
                                                    <div
                                                        key={movie.id}
                                                        onClick={() => handleMovieClick(movie)}
                                                        className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer group max-w-sm mx-auto"
                                                    >
                                                        {/* Poster */}
                                                        <div className="relative aspect-[2/3] bg-gradient-to-br from-purple-400 via-purple-500 to-indigo-600">
                                                            {(movie.posterUrl || parsedInfo.posterUrl) && (movie.posterUrl || parsedInfo.posterUrl) !== 'https://via.placeholder.com/400x600/6366f1/ffffff?text=Film+Posteri' ? (
                                                                <img
                                                                    src={movie.posterUrl || parsedInfo.posterUrl}
                                                                    alt={movie.title}
                                                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                                                />
                                                            ) : (
                                                                <div className="flex items-center justify-center h-full text-white bg-gradient-to-br from-purple-500 via-indigo-600 to-blue-700 group-hover:scale-105 transition-transform duration-300">
                                                                    <div className="text-center p-3">
                                                                        <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3 border border-white/30">
                                                                            <Film className="h-10 w-10 mx-auto mb-2 opacity-90" />
                                                                            <p className="text-xs font-bold mb-1">{movie.title}</p>
                                                                            <p className="text-xs opacity-80">{parsedInfo.year || movie.year}</p>
                                                                            <div className="mt-1 flex items-center justify-center space-x-1">
                                                                                <span className="text-yellow-300 text-xs">⭐</span>
                                                                                <span className="text-xs font-medium">{parsedInfo.rating || movie.rating}</span>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {/* Rating Badge */}
                                                            <div className="absolute top-3 right-3 bg-black bg-opacity-70 text-white px-2 py-1 rounded-full">
                                                                <span className="font-bold text-sm">🌟 {parsedInfo.rating || movie.rating}</span>
                                                            </div>

                                                            {/* Durum Göstergeleri */}
                                                            <div className="absolute top-3 left-3 flex flex-col gap-2">
                                                                {isFavorite(movie.id, movie.title) && (
                                                                    <div className="bg-red-500 bg-opacity-90 text-white p-2 rounded-full shadow-lg">
                                                                        <Heart className="h-4 w-4 fill-current" />
                                                                    </div>
                                                                )}
                                                                {isWatched(movie.id) && (
                                                                    <div className="bg-green-500 bg-opacity-90 text-white p-2 rounded-full shadow-lg">
                                                                        <Eye className="h-4 w-4 fill-current" />
                                                                    </div>
                                                                )}
                                                            </div>

                                                            {/* Click Indicator */}
                                                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center">
                                                                <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white bg-opacity-90 rounded-full p-2">
                                                                    <Search className="h-5 w-5 text-purple-600" />
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Content */}
                                                        <div className="p-4">
                                                            <h4 className="font-bold text-gray-900 dark:text-white mb-2 text-lg group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors">
                                                                🎬 {parsedInfo.title || movie.title}
                                                            </h4>

                                                            <div className="flex items-center justify-between text-sm mb-3">
                                                                <span className="text-gray-600 dark:text-gray-400">
                                                                    📆 {parsedInfo.year || movie.year}
                                                                </span>
                                                                <span className="px-2 py-1 bg-gradient-to-r from-purple-100 to-indigo-100 dark:from-purple-900/30 dark:to-indigo-900/30 text-purple-800 dark:text-purple-300 rounded-full text-xs font-medium">
                                                                    {movie.genre}
                                                                </span>
                                                            </div>

                                                            <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-3">
                                                                📝 {parsedInfo.explanation || movie.description}
                                                            </p>

                                                            {/* Favori ve İzleme Butonları */}
                                                            <div className="flex gap-2 mb-3">
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation(); // Kartın tıklama eventi ile karışmasın
                                                                        handleFavoriteToggle(movie);
                                                                    }}
                                                                    className={`flex-1 flex items-center justify-center space-x-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-all transform hover:scale-105 ${isFavorite(movie.id, movie.title)
                                                                        ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-700'
                                                                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-600 hover:bg-red-50 dark:hover:bg-red-900/20'
                                                                        }`}
                                                                >
                                                                    <Heart className={`h-4 w-4 ${isFavorite(movie.id, movie.title) ? 'fill-current' : ''}`} />
                                                                    <span className="hidden sm:inline">{isFavorite(movie.id, movie.title) ? 'Favori' : 'Favori'}</span>
                                                                </button>

                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation(); // Kartın tıklama eventi ile karışmasın
                                                                        handleWatchedToggle(movie);
                                                                    }}
                                                                    className={`flex-1 flex items-center justify-center space-x-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-all transform hover:scale-105 ${isWatched(movie.id)
                                                                        ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 border border-green-200 dark:border-green-700'
                                                                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-600 hover:bg-green-50 dark:hover:bg-green-900/20'
                                                                        }`}
                                                                >
                                                                    {isWatched(movie.id) ? (
                                                                        <Eye className="h-4 w-4 fill-current" />
                                                                    ) : (
                                                                        <EyeOff className="h-4 w-4" />
                                                                    )}
                                                                    <span className="hidden sm:inline">{isWatched(movie.id) ? t('favorites.watchedShort') : t('favorites.markAsWatched')}</span>
                                                                </button>
                                                            </div>

                                                            {/* Fragman ve Google Ara */}
                                                            <div className="flex gap-2">
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation(); // Kartın tıklama eventi ile karışmasın
                                                                        handleWatchTrailer(movie);
                                                                    }}
                                                                    disabled={trailerLoading && currentMovieForTrailer === movie.title}
                                                                    className="flex-1 flex items-center justify-center space-x-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-all transform hover:scale-105 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-700 hover:bg-blue-200 dark:hover:bg-blue-900/50 disabled:opacity-50"
                                                                >
                                                                    {trailerLoading && currentMovieForTrailer === movie.title ? (
                                                                        <Loader className="h-4 w-4 animate-spin" />
                                                                    ) : (
                                                                        <Play className="h-4 w-4" />
                                                                    )}
                                                                    <span className="hidden sm:inline">
                                                                        {trailerLoading && currentMovieForTrailer === movie.title
                                                                            ? t('favorites.searching')
                                                                            : t('favorites.trailer')
                                                                        }
                                                                    </span>
                                                                </button>

                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        e.preventDefault();
                                                                        openGoogleSearch(movie.title || movie.name);
                                                                    }}
                                                                    className="flex-1 flex items-center justify-center space-x-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-all transform hover:scale-105 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 border border-purple-200 dark:border-purple-700 hover:bg-purple-200 dark:hover:bg-purple-900/50"
                                                                >
                                                                    <Search className="h-4 w-4" />
                                                                    <span className="hidden sm:inline">{t('favorites.search')}</span>
                                                                </button>
                                                            </div>

                                                            <div className="text-center">
                                                                <span className="text-xs text-purple-600 dark:text-purple-400 opacity-70">
                                                                    👆 {t('favorites.clickForDetails')}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mt-6">
                    <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                            Toplam {filteredRecommendations.length} öneri,
                            Sayfa {currentPage} / {totalPages}
                        </div>

                        <div className="flex items-center space-x-2">
                            {/* Önceki sayfa */}
                            <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                className={`px-3 py-2 rounded-lg border transition-colors ${currentPage === 1
                                    ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-600 cursor-not-allowed border-gray-200 dark:border-gray-600'
                                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 border-gray-300 dark:border-gray-600'
                                    }`}
                            >
                                Önceki
                            </button>

                            {/* Sayfa numaraları */}
                            <div className="flex items-center space-x-1">
                                {getPaginationNumbers().map((pageNumber) => (
                                    <button
                                        key={pageNumber}
                                        onClick={() => handlePageChange(pageNumber)}
                                        className={`w-10 h-10 rounded-lg font-medium transition-colors ${pageNumber === currentPage
                                            ? 'bg-purple-600 text-white'
                                            : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-purple-50 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-600'
                                            }`}
                                    >
                                        {pageNumber}
                                    </button>
                                ))}
                            </div>

                            {/* Sonraki sayfa */}
                            <button
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                className={`px-3 py-2 rounded-lg border transition-colors ${currentPage === totalPages
                                    ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-600 cursor-not-allowed border-gray-200 dark:border-gray-600'
                                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 border-gray-300 dark:border-gray-600'
                                    }`}
                            >
                                Sonraki
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal */}
            {selectedMovie && (
                <MovieDetailModal
                    movie={selectedMovie}
                    onClose={closeModal}
                />
            )}

            {/* Trailer Modal */}
            <TrailerModal
                trailer={selectedTrailer}
                isOpen={isTrailerModalOpen}
                onClose={closeTrailerModal}
                movieTitle={currentMovieForTrailer}
            />

            {/* İstatistikler */}
            <div className="mt-8 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-lg shadow p-6 text-white">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="text-center">
                        <div className="text-2xl font-bold">{recommendations.length}</div>
                        <div className="text-purple-100">Toplam Öneri</div>
                    </div>
                    <div className="text-center">
                        <div className="text-2xl font-bold">
                            {recommendations.reduce((sum, rec) => sum + rec.recommendations.length, 0)}
                        </div>
                        <div className="text-purple-100">Toplam Film</div>
                    </div>
                    <div className="text-center">
                        <div className="text-2xl font-bold">{getAllGenres().length}</div>
                        <div className="text-purple-100">Farklı Tür</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HistoryPage; 